import cn from "classnames"

import { Flex, Text } from "@/atoms"
import * as ApplicationDomain from "@/domain/application"
import { ApplicationTag } from "@/molecules/ApplicationTag"
import { MoreTableCell } from "@/molecules/MoreTableCell"
import { APPLICATION_DRAWER_ID } from "@/organisms/Drawers/const"
import { ImpactExternalLink } from "./ImpactExternalLink"
import { ImpactPopover } from "./ImpactPopover"
import { TitleImpactPopover } from "./TitleImpactPopover"
import { getApplicationKey } from "./helpers"

import styles from "./ImpactCell.module.scss"

import type { DataImage } from "@/api/useInventory.types"
import { NO_VALUE_PLACEHOLDER } from "@/const/default-values"
import type { Issue, Project } from "@/domain/issue"
import type { RowClick } from "@/hooks/useLinkDrawerTooltip"
import type { TFunction } from "@/hooks/useTranslation"
import type {
  ApplicationImpact,
  ItemApplicationImpact
} from "@/pages/TriagePage/application/useTriage.types"

export const ImpactCell = (
  attr: { t?: TFunction; onRowClick?: RowClick } = {}
) =>
  function ImpactCellComponent(
    props: ApplicationImpact,
    record: Project | Issue | DataImage
  ) {
    if (!props) return null
    if (props.noData) return NO_VALUE_PLACEHOLDER

    const noLabel = props.label === undefined || props.label === null

    if (noLabel) {
      const { items, active, inactive, showAllApplications } = props
      const isRelatedApplications = "relatedApplications" in record
      const relatedApplications =
        (isRelatedApplications
          ? record.relatedApplications
          : record.applications) || []
      const label = active + inactive

      return (
        <ImpactPopover
          title={<TitleImpactPopover {...props} />}
          items={items || []}
          onRowClick={attr.onRowClick}
          renderItem={(item, openDrawer) => {
            const application = (
              typeof item === "number" ? relatedApplications?.[item] : item
            ) as ApplicationImpact & {
              id: string
              applicationId?: string
              description?: string
            }

            const {
              name,
              workloadStatus = "terminated",
              description = ApplicationDomain.getDescription(application)
            } = application
            const key = getApplicationKey(application)

            return (
              <div
                key={key}
                className={cn(
                  styles.item,
                  !!openDrawer && styles.itemClickable
                )}
                onClick={() => {
                  if (key) openDrawer?.(APPLICATION_DRAWER_ID, key)
                }}
              >
                <ApplicationTag onlyInkTag type={workloadStatus} />
                <Text
                  ellipsis={{
                    tooltip: {
                      title: name,
                      rootClassName: "root-class-tooltip-antd-ks"
                    }
                  }}
                >
                  {name}
                </Text>
                <Text
                  ellipsis={{
                    tooltip: {
                      title: description,
                      rootClassName: "root-class-tooltip-antd-ks"
                    }
                  }}
                  className={styles.itemDescription}
                >
                  {description}
                </Text>
              </div>
            )
          }}
          footer={
            isRelatedApplications &&
            showAllApplications && (
              <Flex justify="center">
                <ImpactExternalLink record={record} />
              </Flex>
            )
          }
        >
          <span className={styles.label}>{label}</span>
        </ImpactPopover>
      )
    }

    const { label, ...restProps } = props as {
      label: string
      items: ItemApplicationImpact[]
    }

    return (
      <MoreTableCell
        label={
          typeof label === "number" && attr.t
            ? `${label} ${attr.t("general.app", { count: label })}`
            : label
        }
        t={attr.t}
        onRowClick={attr.onRowClick}
        {...restProps}
      />
    )
  }
